// ©2024 Austin App House. All rights reserved.
//
// Save a US bank account on the Payment Methods page (no charge).
//
import React, { useState } from 'react';
import Notification from '../../notification';
import CustomSelect from '../../layout/CustomSelect';
import { white as customSelectStyle } from '../../../utils/customSelectStyle';
import { saveBankAccount } from '../../../utils/paymentBanks';

const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Checking', value: 'checking' },
  { label: 'Savings', value: 'savings' },
];

export default function SaveBankAccount({ onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountType, setAccountType] = useState(ACCOUNT_TYPE_OPTIONS[0]);
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  const handleRoutingNumber = (e) => {
    setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9));
  };

  const handleAccountNumber = (e) => {
    setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 17));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bankName.trim()) {
      Notification('error', { message: 'Please enter the bank name' });
      return;
    }
    if (!accountHolderName.trim()) {
      Notification('error', { message: 'Please enter the account holder name' });
      return;
    }
    if (routingNumber.length !== 9) {
      Notification('error', { message: 'Routing number must be exactly 9 digits' });
      return;
    }
    if (accountNumber.length < 4) {
      Notification('error', { message: 'Please enter a valid account number' });
      return;
    }

    setSubmitting(true);
    try {
      const result = await saveBankAccount({
        bankName,
        accountHolderName,
        accountType: accountType.value,
        routingNumber,
        accountNumber,
      });

      if (result?.success === false) {
        throw new Error(result.message || result.error || 'Failed to save bank account');
      }

      Notification('success', {
        message: result?.message || 'Bank account saved successfully.',
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      Notification('error', {
        message: err.response?.data?.error || err.response?.data?.message || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form id="saveBankAccountForm" onSubmit={handleSubmit}>
      <div className="field_row">
        <div className="field_name">
          <label htmlFor="saveBankName">Bank Name</label>
        </div>
        <div className="field_block">
          <input
            id="saveBankName"
            type="text"
            placeholder="e.g. Chase, Bank of America"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="saveAccountHolderName">Account Holder Name</label>
        </div>
        <div className="field_block">
          <input
            id="saveAccountHolderName"
            type="text"
            placeholder="Full name on bank account"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label>Account Type</label>
        </div>
        <div className="field_block">
          <CustomSelect
            styles={customSelectStyle}
            options={ACCOUNT_TYPE_OPTIONS}
            value={accountType}
            onChange={(option) => setAccountType(option)}
            isSearchable={false}
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="saveRoutingNumber">Routing Number</label>
        </div>
        <div className="field_block">
          <input
            id="saveRoutingNumber"
            type="text"
            inputMode="numeric"
            placeholder="9-digit routing number"
            value={routingNumber}
            onChange={handleRoutingNumber}
            maxLength={9}
          />
        </div>
      </div>

      <div className="field_row">
        <div className="field_name">
          <label htmlFor="saveAccountNumber">Account Number</label>
        </div>
        <div className="field_block">
          <input
            id="saveAccountNumber"
            type="text"
            inputMode="numeric"
            placeholder="Bank account number"
            value={accountNumber}
            onChange={handleAccountNumber}
            maxLength={17}
          />
        </div>
      </div>

      <div className="field_row" style={{ marginTop: '12px' }}>
        <button
          type="submit"
          className="standard_btn light_btn"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save Bank Account'}
        </button>
      </div>
    </form>
  );
}
